/**
 * Hook para validación de cuotas del plan
 * Proporciona funcionalidad para validar límites y mostrar modal de pago
 */
import { useState, useCallback } from 'react';
import { useEmpresaStats } from './useEmpresaStats';
import toastService from '@/utils/toastService';

export const usePlanValidation = () => {
  const { usageMetrics, upgradeRecommendation, loading } = useEmpresaStats();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [targetPlanId, setTargetPlanId] = useState(null);
  const [blockingResource, setBlockingResource] = useState(null);

  /**
   * Valida si se puede crear un recurso según las cuotas del plan
   * @param {string} resourceType - Tipo de recurso (sucursales, clientes, vehiculos, mantenimientos)
   * @param {Object} options - Opciones adicionales
   * @param {boolean} options.showToast - Mostrar toast de error (default: true)
   * @param {boolean} options.showModal - Mostrar modal de upgrade (default: true)
   * @returns {boolean} - true si puede crear, false si está en el límite
   */
  const validateQuota = useCallback((resourceType, options = {}) => {
    const { showToast = true, showModal = true, silent = false } = options;

    // Si está cargando estadísticas, permitir la operación (fail-safe)
    if (loading) {
      //console.log('🔄 Estadísticas cargando, permitiendo operación (fail-safe)');
      return true;
    }

    // Si no hay métricas disponibles, permitir la operación (fail-safe)
    if (!usageMetrics || !usageMetrics[resourceType]) {
      //console.log('❌ No hay métricas disponibles, permitiendo operación (fail-safe)');
      return true;
    }

    const metric = usageMetrics[resourceType];
    
    console.log(`📊 Validando cuota para ${resourceType}:`, {
      actual: metric.actual,
      max: metric.max,
      percentage: metric.percentage,
      isAtLimit: metric.isAtLimit,
      remaining: metric.remaining
    });

    // Si está en el límite, bloquear la operación
    if (metric.isAtLimit) {
      setBlockingResource(resourceType);
      
      // Mostrar toast de error si no está en modo silencioso
      if (showToast && !silent) {
        const resourceNames = {
          sucursales: 'sucursales',
          clientes: 'clientes', 
          vehiculos: 'vehículos',
          mantenimientos: 'mantenimientos'
        };
        
        toastService.error(
          `Has alcanzado el límite de ${resourceNames[resourceType]} (${metric.actual}/${metric.max}) para tu plan actual. Mejora tu plan para continuar.`
        );
      }

      // Mostrar modal de upgrade si está disponible
      if (showModal && upgradeRecommendation?.targetPlan) {
        setTargetPlanId(upgradeRecommendation.targetPlan.id);
        setShowPaymentModal(true);
      }

      return false;
    }

    // Si está cerca del límite (80%+), mostrar advertencia pero permitir operación
    if (metric.isNearLimit && showToast && !silent) {
      const resourceNames = {
        sucursales: 'sucursales',
        clientes: 'clientes',
        vehiculos: 'vehículos', 
        mantenimientos: 'mantenimientos'
      };
      
      toastService.warning(
        `Estás usando ${metric.actual} de ${metric.max} ${resourceNames[resourceType]} permitidos. Te quedan ${metric.remaining} disponibles.`
      );
    }

    return true;
  }, [usageMetrics, upgradeRecommendation, loading]);

  /**
   * Valida múltiples recursos a la vez
   * @param {Array<string>} resourceTypes - Array de tipos de recursos a validar
   * @param {Object} options - Opciones adicionales
   * @returns {Object} - Objeto con resultado de validación para cada recurso
   */
  const validateMultipleQuotas = useCallback((resourceTypes, options = {}) => {
    const { silent = false } = options;
    const results = {};
    let allValid = true;

    for (const resourceType of resourceTypes) {
      const result = validateQuota(resourceType, { ...options, silent });
      results[resourceType] = {
        valid: result,
        metric: usageMetrics?.[resourceType] || null
      };
      
      if (!result) {
        allValid = false;
      }
    }

    // Si alguno falló y se debe mostrar modal, mostrar para el recurso recomendado
    if (!allValid && options.showModal !== false && upgradeRecommendation?.targetPlan) {
      setTargetPlanId(upgradeRecommendation.targetPlan.id);
      setShowPaymentModal(true);
    }

    return {
      allValid,
      results
    };
  }, [validateQuota, usageMetrics, upgradeRecommendation]);

  /**
   * Obtiene información de uso para un recurso específico
   * @param {string} resourceType - Tipo de recurso
   * @returns {Object|null} - Información de uso del recurso
   */
  const getResourceUsage = useCallback((resourceType) => {
    return usageMetrics?.[resourceType] || null;
  }, [usageMetrics]);

  /**
   * Verifica si un recurso está cerca del límite
   * @param {string} resourceType - Tipo de recurso
   * @returns {boolean} - true si está cerca del límite (80%+)
   */
  const isNearLimit = useCallback((resourceType) => {
    const metric = usageMetrics?.[resourceType];
    return metric?.isNearLimit || false;
  }, [usageMetrics]);

  /**
   * Verifica si un recurso está en el límite
   * @param {string} resourceType - Tipo de recurso
   * @returns {boolean} - true si está en el límite
   */
  const isAtLimit = useCallback((resourceType) => {
    const metric = usageMetrics?.[resourceType];
    return metric?.isAtLimit || false;
  }, [usageMetrics]);

  /**
   * Cierra el modal de pago
   */
  const closePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setTargetPlanId(null);
    setBlockingResource(null);
  }, []);

  /**
   * Fuerza la apertura del modal de pago con un plan específico
   * @param {string} planId - ID del plan objetivo
   */
  const openPaymentModal = useCallback((planId = null) => {
    const targetId = planId || upgradeRecommendation?.targetPlan?.id;
    if (targetId) {
      setTargetPlanId(targetId);
      setShowPaymentModal(true);
    }
  }, [upgradeRecommendation]);

  return {
    // Funciones principales
    validateQuota,
    validateMultipleQuotas,
    
    // Funciones de información
    getResourceUsage,
    isNearLimit,
    isAtLimit,
    
    // Estado del modal
    showPaymentModal,
    targetPlanId,
    blockingResource,
    
    // Funciones de control del modal
    closePaymentModal,
    openPaymentModal,
    
    // Datos originales
    usageMetrics,
    upgradeRecommendation,
    loading
  };
};

export default usePlanValidation;